import { useState, useEffect } from "react";
import { TrendingUp, Loader2, ChevronRight, Zap, RefreshCw, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";



const LEAGUES = ['POR','ENG','SPA','GER','ITA','FRA','NED','BEL','TUR','SCO','BRA','ARG','UCL','UEL','UCO','WC26','INT','NOR','SWE','MLS','KSA','FIN','LIB'];

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export default function ValueBetsToday({ bankroll = 1000, onFixtureSelect, selectedDate = new Date(), isAdmin = false }) {
  const { t } = useTranslation();
  const [unlockedBets, setUnlockedBets] = useState(new Set());
  const [unlockingBet, setUnlockingBet] = useState(null);
  const isUnlocked = (i) => isAdmin || unlockedBets.has(i);

  const handleWatchAd = (i, e) => {
    e.stopPropagation();
    setUnlockingBet(i);
    window.open('https://omg10.com/4/11029694', '_blank');
    setTimeout(() => {
      setUnlockingBet(null);
      setUnlockedBets(prev => new Set([...prev, i]));
    }, 5000);
  };
  const [valueBets, setValueBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);

  const fetchValueBets = async () => {
    setLoading(true);
    setValueBets([]);
    setProgress(0);

    const all = [];
    let processed = 0;
    let totalFixtures = 0;

    // First pass: collect all today's fixtures
    const todayFixtures = [];
    for (const code of LEAGUES) {
      try {
        const data = await api.getFixtures(code);
        const fixtures = (data.fixtures || []).filter(f => {
          if (!f.date) return false;
          const fd = new Date(f.date);
          fd.setHours(fd.getHours() + 1);
          return isSameDay(fd, selectedDate);
        }).map(f => ({ ...f, league_code: code, league_name: data.league_name || code }));
        todayFixtures.push(...fixtures);
      } catch {}
    }

    totalFixtures = todayFixtures.length;
    setTotal(totalFixtures);

    if (totalFixtures === 0) {
      setLoading(false);
      return;
    }

    // Second pass: get predictions
    for (const fixture of todayFixtures) {
      try {
        const pred = await api.getPrediction(fixture.fixture_id, bankroll);
        const vb = (pred.suggestions || []).filter(s => s.is_value_bet && s.edge >= 5.0);
        vb.forEach(s => all.push({
          fixture_id: fixture.fixture_id,
          time: fixture.date ? new Date(fixture.date).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '',
          league: fixture.league_name,
          league_code: fixture.league_code,
          home: pred.home?.name,
          home_logo: pred.home?.logo,
          away: pred.away?.name,
          away_logo: pred.away?.logo,
          market: s.market,
          odd: s.odd,
          probability: s.probability,
          edge: s.edge,
          stake: s.stake,
        }));
      } catch {}
      processed++;
      setProgress(processed);
      // Update list progressively
      all.sort((a, b) => b.edge - a.edge);
      setValueBets([...all]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchValueBets();
  }, [selectedDate, bankroll]);

  return (
    <div className="bg-surface border border-line rounded-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface-hover">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-volt" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('valueBetsDay')}</h3>
          {valueBets.length > 0 && (
            <span className="bg-volt/20 text-volt text-[10px] font-bold px-2 py-0.5 rounded-sm">
              {valueBets.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {loading && total > 0 && (
            <span className="text-[10px] text-txt-secondary">{progress}/{total} jogos</span>
          )}
          <button onClick={fetchValueBets} disabled={loading} className="p-1 hover:bg-surface rounded-sm transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 text-txt-secondary ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {loading && total > 0 && (
        <div className="h-1 bg-obsidian">
          <div className="h-full bg-volt transition-all" style={{ width: `${(progress/total)*100}%` }} />
        </div>
      )}

      {loading && total === 0 ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Loader2 className="w-5 h-5 text-volt animate-spin" />
          <span className="text-txt-secondary text-sm">{t('loadingGames')}</span>
        </div>
      ) : !loading && valueBets.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <TrendingUp className="w-8 h-8 text-txt-secondary mx-auto mb-2" />
          <p className="text-txt-secondary text-sm">{t('noValueBets')}</p>
          <p className="text-txt-secondary/50 text-xs mt-1">{t('noValueBetsSubtitle')}</p>
        </div>
      ) : (
        <div className="divide-y divide-line/30">
          {valueBets.map((bet, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors">
              <div className="shrink-0 bg-volt/10 border border-volt/30 rounded-sm px-2 py-1 text-center min-w-[52px]">
                <p className="text-volt font-mono font-bold text-sm">+{bet.edge}%</p>
                <p className="text-volt/60 text-[9px] uppercase">edge</p>
              </div>
              <button
                className="flex-1 min-w-0 text-left"
                onClick={() => isUnlocked(i) && onFixtureSelect && bet.fixture_id && onFixtureSelect(bet.fixture_id, bet.league_code)}
              >
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  {bet.home_logo && <img src={bet.home_logo} alt="" className="w-4 h-4 object-contain" />}
                  <span className="text-sm font-semibold truncate">{bet.home}</span>
                  <span className="text-txt-secondary text-xs">vs</span>
                  {bet.away_logo && <img src={bet.away_logo} alt="" className="w-4 h-4 object-contain" />}
                  <span className="text-sm text-txt-secondary truncate">{bet.away}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-txt-secondary">{bet.league}</span>
                  <span className="text-txt-secondary/30">·</span>
                  <span className="text-[10px] text-txt-secondary">{bet.time}</span>
                </div>
              </button>
              <div className="shrink-0 text-right">
                {isUnlocked(i) ? (
                  <>
                    <p className="text-xs font-bold text-white">{({
                      "1": t('homeWinFull'), "X": t('drawFull'), "2": t('awayWinFull'),
                      "over25": t('over25Full'), "under25": t('under25Full'),
                      "btts_yes": t('bttsYes'), "btts_no": t('bttsNo'),
                      "corners_over95": `${t('cornersOver')} 9.5`,
                    })[bet.market] || bet.market}</p>
                    <p className="text-xs text-txt-secondary">@ {bet.odd}</p>
                    <p className="text-[10px] text-volt">{bet.probability}%</p>
                  </>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <div style={{ filter: "blur(4px)" }} className="select-none text-right">
                      <p className="text-xs font-bold text-white">Market</p>
                      <p className="text-xs text-txt-secondary">@ 0.00</p>
                    </div>
                    <button
                      onClick={(e) => handleWatchAd(i, e)}
                      disabled={unlockingBet === i}
                      className="text-[11px] bg-volt text-obsidian font-bold px-3 py-1.5 rounded-sm hover:bg-volt/80 transition-all flex items-center gap-1"
                    >
                      {unlockingBet === i
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading...</>
                        : <>🔓 FREE</>}
                    </button>
                    <button
                      onClick={() => window.open('https://whop.com/betmodel/betmodel-premium/', '_blank')}
                      className="text-[9px] text-txt-secondary hover:text-volt transition-colors"
                    >
                      or Subscribe
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {valueBets.length > 0 && (
        <div className="px-4 py-2 border-t border-line/30 flex items-center gap-1.5">
          <Zap className="w-3 h-3 text-txt-secondary" />
          <p className="text-[10px] text-txt-secondary">Model V9.0 · {t('stakeLabel')}s only · Play responsibly</p>
        </div>
      )}
    </div>
  );
}
